<template>
  <div class="wt2-auto-complete -autocomplete" v-click-outside="setBlur">
    <div
      class="wt2-search-entity"
      v-if="showSearchEntity"
      :class="{ '-disabled': disableInput }"
    >
      <img
        v-if="searchEntity.type === 'USERNAME'"
        :src="mixinProxySrc(searchEntity.photo)"
      />
      <div v-if="searchEntity.type === 'PLACE'">
        <i class="wticon wticon-instagram-location"></i>
      </div>
      <div v-if="searchEntity.type === 'HASHTAG'">
        <i class="wticon wticon-instagram-hashtag"></i>
      </div>
    </div>

    <div class="wt-input-holder wt2-text-l" :class="inputClasses">
      <div
        class="wt2-input-entity-clone"
        v-if="showSearchEntity && searchEntity.verified"
        type="text"
      >
        {{ tempValue }}
        <img
          src="/resources/img/instagram_verified.png"
          class="wt-inst-verified"
        />
      </div>

      <input
        ref="input"
        type="text"
        :value="tempValue"
        :placeholder="placeholder"
        :class="inputClasses"
        @input="onInput($event.target.value)"
        @mousedown="touched = true"
        @keydown="touched = true"
        :disabled="disableInput"
        @focus="setFocus"
      />
    </div>

    <div class="wt2-color-error -error-message" v-if="showErrorMessage">
      Something isn’t working,&nbsp;
      <span @click="retrySearchSuggestions">give it another try</span>
    </div>

    <div class="-results" v-show="showResultsPanel">
      <ul ref="results" :class="{ '-instagram': true }">
        <wt-instagram-entity
          v-for="(entity, key) in suggestionsFromServer"
          class="wt2-instagram-entity-results"
          :class="{ '-active': key === activeRowIndex }"
          :entity="entity"
          :key="entity.id"
          @mouseenter.native="onHoverOnOption(key)"
          @mousedown.native="search({ entity }, $event)"
        ></wt-instagram-entity>
      </ul>
    </div>
    <wt-google-like-loader
      v-if="showSpinner"
      class="-default"
    ></wt-google-like-loader>

    <transition name="wt2-fade-fast">
      <div v-show="tempValue.length" class="-clear" @click="clearTextBox">
        <div>
          <span class="wticon wticon-close_x"></span>
        </div>
      </div>
    </transition>
    <div class="-search-spacer"></div>
  </div>
</template>

<script>
import app from 'app';
import AutoCompleteMixins from './autoCompleteMixin.vue';
import InstagramEntity from 'v/mediaFiltering/InstagramEntity.vue';

export default {
  components: {
    'wt-instagram-entity': InstagramEntity,
  },
  mixins: [AutoCompleteMixins],
  data: function () {
    return {
      tempEntity: null,
      suggestionsFromServer: [],
      loadingSuggestions: false,
      hasSuggestionsError: false,
    };
  },
  props: {
    searchEntity: {
      type: Object,
    },
    numSuggestions: {
      type: Number,
      default: 5,
    },
    acPromise: {
      type: Function,
      required: false,
      default: _.noop,
    },
    queryRequired: {
      type: Boolean,
      default: true,
    },
  },

  mounted() {
    this.suggestionsFromServer = [];
    this.getSuggestionsFromServer(this.value);
  },

  watch: {
    value(newVal) {
      this.tempValue = newVal;
      this.tempEntity = null;
      if (newVal) {
        this.getSuggestionsFromServer(newVal);
      }
    },

    suggestionsFromServer(newVal) {
      this.tempEntity = newVal[0];
      this.activeRowIndex = 0;
      this.$emit('response', newVal);
    },

    focused() {
      this.hasSuggestionsError = false;
    },
  },
  computed: {
    query() {
      const value = this.value || '';
      return value.replace(/["']/g, '').trim();
    },
    inputClasses() {
      return {
        'wt-input': true,
        '-with-entity': this.showSearchEntity,
        [this.inputClass]: true,
        '-with-button': !!this.button,
      };
    },
    showSearchEntity() {
      return !!this.searchEntity && !this.focused && !this.showResultsPanel;
    },
    showErrorMessage() {
      return (
        this.hasSuggestionsError &&
        this.touched &&
        !this.showResultsPanel &&
        !this.showSearchEntity
      );
    },
    showSpinner() {
      return (
        this.loadingSuggestions &&
        this.touched &&
        !this.showSearchEntity &&
        this.tempValue.length
      );
    },
    allSuggestions() {
      const suggestions = this.suggestionsFromServer;
      return [...suggestions];
    },
    canSearch() {
      return !_.isEmpty(this.tempValue);
    },
  },
  beforeDestroy() {
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.TLLB);
  },
  methods: {
    onInput(newVal) {
      this.hasSuggestionsError = false;
      this.tempEntity = null;
      this.tempValue = newVal;
      this.highlight = newVal;
      if (!this.disabled) {
        this.getSuggestionsFromServer(newVal);
      }

      if (this.searchEntity && this.searchEntity.text != this.tempValue) {
        this.$emit('clear');
      }
    },

    retrySearchSuggestions() {
      this.getSuggestionsFromServer(this.tempValue);
    },

    onEnterPressed(e) {
      if (this.tempEntity) {
        this.search({ entity: this.tempEntity }, e);
      }
    },
    getSuggestionsFromServer(value) {
      this.loadingSuggestions = true;
      this.hasSuggestionsError = false;
      value = (value || '').replace(/["']/g, '').trim();
      this.acPromise(value)
        .then((response) => {
          this.suggestionsFromServer = response;
          this.loadingSuggestions = false;
        })
        .catch((response) => {
          this.suggestionsFromServer = [];
          if (response) {
            console.error('Error getting Instagram entities: ', response);
            this.hasSuggestionsError = true;
            this.loadingSuggestions = false;
          }
        });
    },
    search({ entity }, e) {
      if (entity) {
        const displayText = entity.name || entity.query;
        this.$emit('input', displayText);
        this.tempValue = displayText;
        this.$emit('search', {
          entity,
          e,
          activeRowIndex: this.activeRowIndex,
        });
      } else {
        this.$emit('search');
      }
      this.focused = false;
      this.activeRowIndex = null;
    },
    arrowPressed(offset) {
      if (this.disabled || !this.allSuggestions.length) {
        return;
      }
      if (this.activeRowIndex === null) {
        this.activeRowIndex = offset > 0 ? 0 : this.allSuggestions.length - 1;
      } else {
        this.activeRowIndex += offset;
      }
      if (this.activeRowIndex === this.allSuggestions.length) {
        this.activeRowIndex = 0;
      }
      if (this.activeRowIndex < 0) {
        this.activeRowIndex = this.allSuggestions.length - 1;
      }
      this.tempValue = this.allSuggestions[this.activeRowIndex].text;
      this.tempEntity = this.allSuggestions[this.activeRowIndex];
      const el = this.$refs.results.childNodes[this.activeRowIndex];
      el.scrollIntoView({ block: 'center' });
    },

    forceFocus() {
      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },

    clearTextBox(e) {
      this.hasSuggestionsError = false;
      this.tempValue = '';
      this.suggestionsFromServer = [];
      this.$emit('input', '');
      this.search({ text: '' }, e);
      this.$emit('clear');
    },
  },
};
</script>
